import { Flex, Select, Text } from "@mantine/core";
import { useMemo } from "react";
import { twMerge } from "tailwind-merge";

import { useUserContext } from "@/context/user.context";
import {
  useCompaniesListQuery,
  useCompanyImpersonationMutation,
} from "@/hooks/queries/company.queries";
import { useGetInternalOnlyRender } from "@/hooks/use-internal-only-render";
import { LAYOUT_DIMENSIONS } from "@/utils/ui";

interface ImpersonationBannerProps {
  disabled?: boolean;
}

// Per product, certain Upwage users are not permitted to impersonate specific
// companies, both listed below.

// external upwage users to filter out
const externalUsers = [
  "daisy@upwage.com",
  "jihan@upwage.com",
  "leonel@upwage.com",
  // "your-email@upwage.com", // for testing
];
// companies to filter out for external upwage users
const companyIds = [
  "c611430f-9184-452b-a803-a4fa2ac85e51", // Compass staging
  "baaa188f-6c17-43fe-b70d-6cfa60475f69", // Compass production
  "7953febe-7af9-4791-b1ed-81246c660fe3", // Discount Tire
  "6e3a2080-d876-4b97-8645-d5b0cd1f3caa", // Paramount
];

export const ImpersonationBanner = ({
  disabled = false,
}: ImpersonationBannerProps) => {
  const { user } = useUserContext();
  const { data: companies = [], isLoading } = useCompaniesListQuery({
    enabled: !!user?.isInternal,
  });
  const { mutate: changeCompany } = useCompanyImpersonationMutation();
  const internalOnlyRender = useGetInternalOnlyRender();

  const isFilteredUser = useMemo(
    () => !user?.email || externalUsers.includes(user.email),
    [user?.email],
  );
  const companyOptions = useMemo(() => {
    const companyList = isFilteredUser
      ? companies.filter((company) => !companyIds.includes(company.id))
      : companies;

    return companyList.map(({ id, name }) => ({ label: name, value: id }));
  }, [companies, isFilteredUser]);

  const isReady = !!user && !isLoading;

  return internalOnlyRender(
    <Flex
      bg="yellow.2"
      className="flex-row items-center px-[18px] py-[9px] w-full"
      style={{ height: LAYOUT_DIMENSIONS.IMPERSONATION_BANNER_HEIGHT_PX }}
    >
      <Text size="sm">{isReady ? "Viewing as a member of" : "Loading..."}</Text>
      {isReady && (
        <Select
          allowDeselect={false}
          className={twMerge([
            "bg-transparent",
            "font-semibold",
            "ml-[8px]",
            "rounded-md",
            "px-[10px]",
            "py-[6px]",
            "text-sm",
            "w-96",
          ])}
          data={companyOptions}
          data-testid="impersonation-banner__company-select"
          defaultValue={user.companyImpersonationId ?? user.company.id}
          disabled={disabled}
          onChange={(companyId) => {
            if (companyId) {
              changeCompany(companyId);
            }
          }}
          onFocus={(e) => e.target.select()}
          searchable
        />
      )}
    </Flex>,
  );
};
